import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { plans as plansApi } from '@/api/plans';
import type { PlanRow } from '@/api/plans';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PlanDialog } from './PlanDialog';
import { Navigate } from 'react-router-dom';
import {
  Plus, MoreHorizontal, Pencil, Trash2, CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/cn';

function formatPrice(cents: number) {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full',
      active
        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900'
        : 'bg-muted text-muted-foreground border border-border',
    )}>
      {active
        ? <CheckCircle2 className="h-3 w-3" />
        : <AlertCircle className="h-3 w-3" />}
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function PlansListInner() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<PlanRow | null>(null);
  const [deletePlan, setDeletePlan] = useState<PlanRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['plans', true],
    queryFn: () => plansApi.list(true),
    select: (d) => d.rows,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => plansApi.delete(id),
    onSuccess: () => {
      toast({ title: 'Plan deleted.' });
      void qc.invalidateQueries({ queryKey: ['plans'] });
      setDeletePlan(null);
    },
    onError: (err: Error & { code?: string }) => {
      const msg = err.message?.includes('plan_in_use')
        ? 'One or more resellers are subscribed to this plan. Reassign them first.'
        : err.message ?? 'Delete failed';
      toast({ title: 'Cannot delete', description: msg, variant: 'destructive' });
    },
  });

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Subscription Plans"
        description="Platform-wide plans available for resellers to subscribe to."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New plan
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                {['Plan', 'Price / mo', 'Channels', 'Minutes', 'Storage', 'DIDs', 'Stripe Price ID', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-muted-foreground text-sm">
                    No plans yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                (data ?? []).map((plan) => (
                  <tr key={plan.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 font-medium">{plan.name}</td>
                    <td className="px-4 py-3 tabular-nums">{formatPrice(plan.monthly_price_cents)}</td>
                    <td className="px-4 py-3 tabular-nums">{plan.included_channels}</td>
                    <td className="px-4 py-3 tabular-nums">{plan.included_minutes.toLocaleString()}</td>
                    <td className="px-4 py-3 tabular-nums">{plan.included_storage_gb} GB</td>
                    <td className="px-4 py-3 tabular-nums">{plan.included_active_dids}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {plan.stripe_price_id
                        ? <span className="text-foreground/80">{plan.stripe_price_id}</span>
                        : <span className="italic">not set</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge active={plan.active} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditPlan(plan)}>
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeletePlan(plan)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create dialog */}
      <PlanDialog open={createOpen} onOpenChange={setCreateOpen} />

      {/* Edit dialog */}
      <PlanDialog
        open={!!editPlan}
        onOpenChange={(o) => { if (!o) setEditPlan(null); }}
        existing={editPlan}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deletePlan} onOpenChange={(o) => { if (!o) setDeletePlan(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete plan?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <strong>{deletePlan?.name}</strong> will be soft-deleted and no longer available to new resellers.
            Resellers already on this plan must be migrated first.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => deletePlan && deleteMutation.mutate(deletePlan.id)}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PlansList() {
  const user = useCurrentUser();
  if (!user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return <PlansListInner />;
}
